import DayPicker from '@rio-cloud/rio-uikit/DayPicker';
import { type Control, useController } from 'react-hook-form';

import { localJSDateToIso } from '#lib/pickerDateTime.ts';
import { RIODateTime } from '#lib/RIODateTime.ts';

import { FormGroup } from './FormGroup.tsx';
import { ValidationViolation } from './ValidationViolation.tsx';

type DatePickerProps = {
    className?: string;
    // biome-ignore lint/suspicious/noExplicitAny: <react-hook-form> types
    control?: Control<any>;
    name: string;
    label?: string;
    allowPastDates?: boolean;
    minDate?: Date;
    fieldValueMode?: 'iso' | 'date' | 'dayIso';
};

type FieldValueMode = NonNullable<DatePickerProps['fieldValueMode']>;

const DATE_FORMAT = RIODateTime.PREDEFINED_FORMATS.GERMAN_DATE;
const DAY_ISO_FORMAT = RIODateTime.PREDEFINED_FORMATS.DAY_ISO;
const DAY_PICKER_DATE_FORMAT = 'dd.MM.yyyy';

const parseFieldValue = (value: string, mode: FieldValueMode) => {
    if (!value) {
        return undefined;
    }

    switch (mode) {
        case 'date':
            return RIODateTime.TryParseLocal(value, DATE_FORMAT)?.toJSDate();
        case 'dayIso':
            return RIODateTime.TryParseLocal(value, DAY_ISO_FORMAT)?.toJSDate();
        case 'iso':
            return RIODateTime.TryFromISO(value)?.toJSDate();
    }
};

const formatFieldValue = (value: Date, mode: FieldValueMode) => {
    switch (mode) {
        case 'date':
            return RIODateTime.FromJSDate(value).toPredefinedFormat(DATE_FORMAT);
        case 'dayIso':
            return RIODateTime.FromJSDate(value).toPredefinedFormat(DAY_ISO_FORMAT);
        case 'iso':
            return localJSDateToIso(value);
    }
};

export const DatePicker = ({
    name,
    className,
    control,
    label,
    allowPastDates = true,
    minDate,
    fieldValueMode = 'iso',
    ...props
}: DatePickerProps) => {
    const { field, fieldState } = useController({ control, name: name });
    const fieldValue = typeof field.value === 'string' ? field.value : '';
    const actualValue = parseFieldValue(fieldValue, fieldValueMode);

    const normalizedMinDate = minDate
        ? new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate(), 0, 0, 0, 0)
        : undefined;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const disabledDays = [
        normalizedMinDate ? { before: normalizedMinDate } : undefined,
        allowPastDates ? undefined : { before: today },
    ].filter((matcher): matcher is { before: Date } => Boolean(matcher));

    const handleChange = (selectedDate: Date | undefined) => {
        if (!selectedDate) {
            field.onChange('');
            return;
        }

        field.onChange(formatFieldValue(selectedDate, fieldValueMode));
    };

    const error = fieldState.error?.message;

    return (
        <FormGroup className={className} hasViolation={!!error}>
            <label htmlFor={name}>{label}</label>
            <DayPicker
                {...props}
                id={name}
                mode='single'
                className={className}
                value={actualValue}
                onChange={handleChange}
                onBlur={field.onBlur}
                closeOnOutsideClick={true}
                closeOnSelect={false}
                defaultMonth={actualValue ?? normalizedMinDate}
                dateFormat={DAY_PICKER_DATE_FORMAT}
                disabledDays={disabledDays.length ? disabledDays : undefined}
                inputProps={{
                    autoComplete: 'off',
                    'aria-label': label,
                }}
            />
            <ValidationViolation violations={error} />
        </FormGroup>
    );
};
